import React, { useState, useEffect } from "react";
import { Plus, Search } from "lucide-react";
import ProductForm from "./ProductForm";
import ProductList from "./ProductList";
import { apiRequest } from "../../config/api";

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    tag: "",
    brand: "",
    is_active: "",
    is_featured: "",
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });

  // Fetch products with filters
  const fetchProducts = async (page = 1) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pagination.per_page.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== "")
        ),
      });

      const response = await apiRequest(`/admin/products?${queryParams}`);

      if (response.success) {
        setProducts(response.data.products);
        setPagination(response.data.pagination);
      } else {
        setError(response.message || "Failed to fetch products");
      }
    } catch (err) {
      setError("Failed to fetch products");
      console.error("Products fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories and tags for filters
  const fetchMetadata = async () => {
    try {
      const [categoriesRes, tagsRes] = await Promise.all([
        apiRequest("/admin/categories?flat=true"),
        apiRequest("/admin/tags"),
      ]);

      if (categoriesRes.success) {
        setCategories(categoriesRes.data.categories);
      }
      if (tagsRes.success) {
        setTags(tagsRes.data.tags);
      }
    } catch (err) {
      console.error("Metadata fetch error:", err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchMetadata();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchProducts(1);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleCreateProduct = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this product? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const response = await apiRequest(`/admin/products/${productId}`, {
        method: "DELETE",
      });

      if (response.success) {
        await fetchProducts(pagination.current_page);
      } else {
        setError(response.message || "Failed to delete product");
      }
    } catch (err) {
      setError("Failed to delete product");
      console.error("Delete error:", err);
    }
  };

  const handleFormSubmit = async () => {
    // Navigate back to product management and refresh list
    setShowForm(false);
    setEditingProduct(null);
    await fetchProducts(pagination.current_page);
  };

  const handlePageChange = (page) => {
    fetchProducts(page);
  };

  if (showForm) {
    return (
      <ProductForm
        product={editingProduct}
        categories={categories}
        tags={tags}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setShowForm(false);
          setEditingProduct(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Product Management
          </h1>
          <p className="text-gray-600">Manage your product catalog</p>
        </div>
        <button
          onClick={handleCreateProduct}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {/* Search */}
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search products..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
            />
          </div>

          {/* Category Filter */}
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange("category", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          {/* Tag Filter */}
          <select
            value={filters.tag}
            onChange={(e) => handleFilterChange("tag", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Tags</option>
            {tags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </select>

          {/* Brand Filter */}
          <input
            type="text"
            placeholder="Filter by brand..."
            value={filters.brand}
            onChange={(e) => handleFilterChange("brand", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          {/* Active Filter */}
          <select
            value={filters.is_active}
            onChange={(e) => handleFilterChange("is_active", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>

          {/* Featured Filter */}
          <select
            value={filters.is_featured}
            onChange={(e) => handleFilterChange("is_featured", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Products</option>
            <option value="true">Featured</option>
            <option value="false">Not Featured</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Product List */}
      <ProductList
        products={products}
        loading={loading}
        pagination={pagination}
        onEdit={handleEditProduct}
        onDelete={handleDeleteProduct}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default ProductManagement;
